import {Close} from "@mui/icons-material";
import {SnackbarKey, useSnackbar} from "notistack";
import React, {useCallback} from "react";
import {IconButtonEx} from "./components/buttons/IconButtonEx";
import {Grid2} from "@mui/material";
import {useTranslation} from "react-i18next";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {CommonI18N} from "@/translations";
import {displayableMessage} from "@/lib/message-key";

/**
 * Minimum `life` value (in ms) above which the snackbar shows a manual close button.
 * Snackbars with `life <= SNACKBAR_CLOSE_BUTTON_THRESHOLD_MS` auto-hide without a dismiss button.
 */
export const SNACKBAR_CLOSE_BUTTON_THRESHOLD_MS = 8000;

export type ShowSnackbarProps = {
    severity: "warning" | "error" | "info" | "success";
    life?: number;
    sticky?: boolean;
    summary: React.ReactNode;
    details?: React.ReactNode;
    list?: string[];
    error?: unknown;
    snackbarKey?: SnackbarKey;
};

/**
 * Extracts the user-facing summary from an Axios error response.
 *
 * Returns `error.response.data.error.message` only when the response body
 * contains a BoxHub-structured error object (`{error: {message: string}}`).
 * Falls back to `fallbackSummary` for Spring-default bodies (`{error: "Bad Request"}`),
 * network errors, any other body shape that does not match, and a message that is an
 * unresolved i18n key — such a key would expose internal key names to the user.
 */
export const extractSnackbarSummary = (error: unknown, fallbackSummary: React.ReactNode): React.ReactNode => {
    const response = (error as { response?: { data?: { error?: unknown } } })?.response;
    const apiError = response?.data?.error;
    if (apiError && typeof apiError === "object" && typeof (apiError as { message?: unknown }).message === "string") {
        return displayableMessage((apiError as { message: string }).message) ?? fallbackSummary;
    }
    return fallbackSummary;
};

export const destruct = (obj: unknown): string[] => {
    const result: string[] = [];
    if (typeof obj === "object" && obj !== null) {
        Object.values(obj as Record<string, unknown>).forEach((value) => {
            if (Array.isArray(value)) {
                result.push(...value);
            } else if (typeof value === "object") {
                result.push(...destruct(value));
            } else if (typeof value === "string") {
                result.push(value);
            }
        });
    }
    return result;
};

const splitToBr = (s?: string) =>
    s?.split("\n").map((line, index) => (
            <span key={`${index}-${line}`}>
            {line}
            <br />
        </span>
    ));

export type DisplayData = {
    summary: React.ReactNode;
    details: React.ReactNode;
    error: unknown;
};

/** Minimal Axios-like error shape used for narrowing inside resolveDisplayData. */
type AxiosLikeError = {
    status?: number;
    message?: string;
    response?: {
        headers?: Record<string, string>;
        data?: {
            error?: unknown;
        };
    };
};

const isAxiosLikeError = (value: unknown): value is AxiosLikeError =>
        typeof value === "object" && value !== null;

/**
 * Maps an Axios error to the display data shown in the snackbar.
 *
 * Handles well-known HTTP status codes (401, 403, 413) with translated messages,
 * falls back to the BoxHub structured error body when available, and otherwise
 * keeps the caller-provided summary unchanged.
 */
export const resolveDisplayData = (
        error: unknown,
        summary: React.ReactNode,
        details: React.ReactNode,
        t: (key: string) => string
): DisplayData => {
    if (!isAxiosLikeError(error)) {
        return {summary, details, error};
    }
    if (error.status === 401) {
        return {
            summary: t(CommonI18N.message.sessionExpiredSummary),
            details: t(CommonI18N.message.sessionExpiredDetails),
            error
        };
    }
    if (error.status === 403) {
        const serverHeader = error.response?.headers?.["server"] ?? "";
        if (/microsoft.*azure.*application.*gateway/i.test(serverHeader)) {
            return {
                summary: t(CommonI18N.message.accessDeniedWAFSummary),
                details: t(CommonI18N.message.accessDeniedWAFDetails),
                error
            };
        }
        return {
            summary: t(CommonI18N.message.accessDeniedSummary),
            details: t(CommonI18N.message.accessDeniedDetails),
            error
        };
    }
    if (error.status === 413) {
        return {
            summary: t(CommonI18N.message.contentTooLargeSummary),
            details: t(CommonI18N.message.contentTooLargeDetails),
            error
        };
    }
    const apiError = error.response?.data?.error;
    if (apiError && typeof apiError === "object" && typeof (apiError as { message?: unknown }).message === "string") {
        const structured = apiError as { message: string; errors?: unknown };
        return {
            // An unresolved message key is dropped: it would expose internal key and constraint
            // names, so the caller-provided summary stays.
            summary: displayableMessage(structured.message) ?? summary,
            details: structured.errors ? destruct(structured.errors) : details,
            error: null
        };
    }
    return {summary, details, error};
};

export const useSnackbarEx = () => {
    const { t } = useTranslation();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const showMessage = useCallback(
        ({ severity, life = 9000, sticky, summary, details, list, error, snackbarKey }: ShowSnackbarProps) => {
            const closeHandler = (snackKey: SnackbarKey) => (
                <IconButtonEx
                    size="small"
                    data-cy="snackbar-close-button"
                    Icon={Close}
                    onClick={() => closeSnackbar(snackKey)}
                />
            );
            ({summary, details, error} = resolveDisplayData(error, summary, details, t));
            if (snackbarKey) {
                closeSnackbar(snackbarKey);
            }
            return enqueueSnackbar(
                <Grid2 container direction="column">
                    <Grid2>{summary}</Grid2>
                    {details && <Grid2>{typeof details === "string" ? splitToBr(details) : details}</Grid2>}
                    {list && (
                        <Grid2>
                            <ul style={{ marginTop: 0, marginBottom: 0, paddingLeft: 24 }}>
                                {list.map((item, index) => (
                                        <li key={`${index}-${item}`}>{item}</li>
                                ))}
                            </ul>
                        </Grid2>
                    )}
                    {process.env.NODE_ENV !== "production" && isAxiosLikeError(error) && error.message && (
                        <Collapsed>
                            <Grid2 sx={{ "&>pre": { marginTop: 1, marginBottom: 0, fontSize: "80%" } }}>
                                <pre>{error.message}</pre>
                            </Grid2>
                        </Collapsed>
                    )}
                </Grid2>,
                {
                    variant: severity,
                    autoHideDuration: sticky ? null : life,
                    action: sticky || life > SNACKBAR_CLOSE_BUTTON_THRESHOLD_MS ? closeHandler : null
                    // preventDuplicate: true,
                }
            );
        },
        [closeSnackbar, enqueueSnackbar, t]
    );

    return { showMessage, enqueueSnackbar, closeSnackbar };
};

const Collapsed = ({ children }: { children: React.ReactNode }) => {
    const {t} = useTranslation();
    const [expanded, setExpanded] = React.useState(false);
    return expanded ? (
        children
    ) : (
            <ButtonEx label={t(CommonI18N.snackbar.action.details)} size="small" variant="text"
                      onClick={() => setExpanded(!expanded)}/>
    );
};
